import React, { useEffect, useMemo, useState } from 'react';
import { connect } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { format, formatDistanceToNow } from '../../utils/datetime';
import {
  Box, Button, Chip, Dialog, DialogActions, DialogContent,
  DialogContentText, DialogTitle, Link, Stack, TextField, Tooltip,
  Typography, alpha, useTheme,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import VisibilityIcon from '@mui/icons-material/Visibility';
import SearchIcon from '@mui/icons-material/Search';
import { SegmentsIcon } from '../global/CustomIcons';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import StorefrontIcon from '@mui/icons-material/Storefront';

import { fetchRequest as segmentsFetch } from '../../redux/actions/segments/list';
import { deleteSegmentRequest as deleteSegment } from '../../redux/actions/segments/delete';
import { previewSegmentByIdRequest, clearPreviewState } from '../../redux/actions/segments/preview';
import { fetchSubscriptions } from '../../redux/actions/marketplace/listings';

import PageBreadcrumbs from '../common/PageBreadcrumbs';
import DataTable from '../common/DataTable';
import ListingToolbar from '../common/ListingToolbar';
import EntityCell from '../common/EntityCell';
import RowActions from '../common/RowActions';
import SegmentDataPreviewModal from '../common/SegmentDataPreviewModal';
import { CMButton } from '../../containers/styledComponents';

const formatTimeValue = (seconds) => {
  if (!seconds) return '—';
  const days = Math.floor(seconds / 86400);
  const hours = Math.floor((seconds % 86400) / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  if (days > 0) return days === 1 ? '1d' : `${days}d`;
  if (hours > 0) return hours === 1 ? '1h' : `${hours}h`;
  if (minutes > 0) return minutes === 1 ? '1m' : `${minutes}m`;
  return seconds === 1 ? '1s' : `${seconds}s`;
};

const formatDate = (d) => {
  if (!d) return '—';
  try { return format(new Date(d), 'MMM d, yyyy'); } catch (_e) { return '—'; }
};

const safeRelative = (d) => {
  try { if (!d) return ''; return `${formatDistanceToNow(new Date(d))} ago`; }
  catch (_e) { return ''; }
};

// Parses ``mkt:<listing_id>:<original_key>`` into ``{listingId, key}``
// or returns ``null`` for non-marketplace strings. Same shape as the
// BE's ``parse_marketplace_key``; we keep it inline so the segment
// list doesn't pull in the full marketplace utils tree.
const parseMarketplaceKey = (s) => {
  if (typeof s !== 'string' || !s.startsWith('mkt:')) return null;
  const parts = s.split(':');
  if (parts.length < 3) return null;
  return { listingId: parts[1], key: parts.slice(2).join(':') };
};

// Inline feature/label list with overflow into +N. Hover reveals all.
// ``subsByListing`` is a {listingId -> subscription} map (provided by
// the parent so it can be built once per render). When an item is a
// ``mkt:`` alias the chip renders ``original_key`` with a storefront
// icon and a "(Listing Title)" caption suffix - mirroring how the
// segment picker / FieldChip already display marketplace signals.
const Pillset = ({ items, color, icon, max = 2, subsByListing = {} }) => {
  const list = Array.isArray(items) ? items : [];
  if (list.length === 0) return null;
  const visible = list.slice(0, max);
  const remaining = list.length - visible.length;

  const labelOf = (item) => {
    if (typeof item !== 'string') return item?.name || '';
    const mkt = parseMarketplaceKey(item);
    if (!mkt) return item;
    const sub = subsByListing[mkt.listingId];
    const display = mkt.key;
    const title = sub?.listing_title;
    return title ? `${display} (${title})` : display;
  };
  const tooltipLabel = (item) => {
    if (typeof item !== 'string') return item?.name || JSON.stringify(item);
    const mkt = parseMarketplaceKey(item);
    if (!mkt) return item;
    const sub = subsByListing[mkt.listingId];
    if (sub?.listing_title) {
      return sub.seller_label
        ? `${mkt.key} · ${sub.listing_title} (by ${sub.seller_label})`
        : `${mkt.key} · ${sub.listing_title}`;
    }
    return `${mkt.key} (marketplace)`;
  };
  const isMarketplace = (item) =>
    typeof item === 'string' && parseMarketplaceKey(item) != null;

  return (
    <Tooltip
      arrow placement="top"
      title={
        <Box sx={{ maxWidth: 360, fontSize: 11 }}>
          {list.map((item, i) => (
            <Box key={i}>{tooltipLabel(item)}</Box>
          ))}
        </Box>
      }
    >
      <Box sx={{ display: 'inline-flex', flexWrap: 'wrap', gap: 0.4, alignItems: 'center' }}>
        {visible.map((item, i) => {
          const mkt = isMarketplace(item);
          return (
            <Chip
              key={i}
              label={labelOf(item)}
              size="small"
              icon={mkt ? <StorefrontIcon sx={{ fontSize: 10 }} /> : icon}
              sx={{
                height: 18,
                fontSize: 10,
                fontWeight: 500,
                bgcolor: color.bg,
                color: color.fg,
                border: `1px solid ${color.bd}`,
                '& .MuiChip-label': { px: 0.5, py: 0 },
                '& .MuiChip-icon': { color: 'inherit', fontSize: 10, ml: '4px' },
              }}
            />
          );
        })}
        {remaining > 0 && (
          <Typography variant="caption" sx={{ fontSize: 10, color: color.fg, fontWeight: 600 }}>
            +{remaining}
          </Typography>
        )}
      </Box>
    </Tooltip>
  );
};

const SegmentsPage = ({
  fetchSegments,
  deleteSegment,
  previewSegmentById,
  clearSegmentPreview,
  payload,
  isLoading,
  error,
  deleteIsLoading,
  previewData,
  previewLoading,
  previewError,
  marketplaceSubscriptions,
  fetchMarketplaceSubscriptions,
}) => {
  const navigate = useNavigate();
  const theme = useTheme();

  // Build {listing_id -> subscription} once per render so every
  // Pillset can render marketplace alias keys with their friendly
  // listing title without each chip re-doing the lookup.
  const subsByListing = useMemo(() => {
    const subs = marketplaceSubscriptions?.subscriptions
      || marketplaceSubscriptions?.data?.subscriptions
      || marketplaceSubscriptions
      || [];
    const list = Array.isArray(subs) ? subs : [];
    const out = {};
    list.forEach((s) => {
      if (s?.listing_id) out[s.listing_id] = s;
    });
    return out;
  }, [marketplaceSubscriptions]);

  const [search, setSearch] = useState('');
  const [typeFilter, setTypeFilter] = useState([]); // 'live' / 'historical'
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [sortColumn, setSortColumn] = useState('created_at');
  const [sortDirection, setSortDirection] = useState('desc');

  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [segmentToDelete, setSegmentToDelete] = useState(null);
  const [deleteQueue, setDeleteQueue] = useState([]);

  const [previewModalOpen, setPreviewModalOpen] = useState(false);
  const [previewPage, setPreviewPage] = useState(1);
  const [previewPageSize, setPreviewPageSize] = useState(100);
  const [selectedSegmentId, setSelectedSegmentId] = useState(null);

  useEffect(() => {
    fetchSegments();
    if (fetchMarketplaceSubscriptions) fetchMarketplaceSubscriptions();
    return () => clearSegmentPreview();
  }, [fetchSegments, clearSegmentPreview, fetchMarketplaceSubscriptions]);

  useEffect(() => {
    if (!deleteIsLoading && deleteQueue.length > 0) setDeleteQueue([]);
  }, [deleteIsLoading]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { setPage(0); }, [search, typeFilter]);

  const segments = payload?.segments || [];

  // Two API endpoints return segments in two different shapes:
  //   * GET /segment          -> raw Mongo doc (live/tail_seconds/interval at TOP level)
  //   * GET /segment/{id}     -> wraps these under ``configuration`` for the detail page
  // The listing page hits the first endpoint, so reading
  // ``s.configuration?.live`` always returned undefined and every segment
  // was rendered as "Historical". Read top-level first, fall back to the
  // nested shape so this works regardless of which endpoint feeds us.
  const segLive = (s) => Boolean(s?.live ?? s?.configuration?.live);
  const segTailSeconds = (s) =>
    Number(s?.tail_seconds ?? s?.configuration?.tail_seconds ?? 0);
  const segInterval = (s) =>
    s?.interval ?? s?.configuration?.interval;

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return segments.filter((s) => {
      if (typeFilter.length > 0) {
        const isLive = segLive(s);
        const wantsLive = typeFilter.includes('live');
        const wantsHistorical = typeFilter.includes('historical');
        if (isLive && !wantsLive) return false;
        if (!isLive && !wantsHistorical) return false;
      }
      if (!q) return true;
      return (s.name || '').toLowerCase().includes(q)
        || (s.description || '').toLowerCase().includes(q)
        || (s.uid || '').toLowerCase().includes(q);
    });
  }, [segments, search, typeFilter]);

  const sorted = useMemo(() => {
    const arr = [...filtered];
    const dir = sortDirection === 'asc' ? 1 : -1;
    arr.sort((a, b) => {
      let av; let bv;
      switch (sortColumn) {
        case 'name': av = (a.name || '').toLowerCase(); bv = (b.name || '').toLowerCase(); break;
        case 'window':
          av = segTailSeconds(a); bv = segTailSeconds(b); break;
        case 'created_at':
        default:
          av = a.created_at ? new Date(a.created_at).getTime() : 0;
          bv = b.created_at ? new Date(b.created_at).getTime() : 0;
      }
      if (av < bv) return -1 * dir;
      if (av > bv) return  1 * dir;
      return 0;
    });
    return arr;
  }, [filtered, sortColumn, sortDirection]);

  const paged = useMemo(
    () => sorted.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage),
    [sorted, page, rowsPerPage],
  );

  const handleAddNewSegment = () => navigate('/segments/create');
  const handleSegmentClick = (segmentId) => navigate(`/segments/${segmentId}`);

  const handleOpenDeleteSegment = (segment) => {
    setSegmentToDelete({ id: segment.uid, name: segment.name });
    setDeleteConfirmOpen(true);
  };
  const handleCloseDeleteDialog = () => {
    setDeleteConfirmOpen(false);
    setSegmentToDelete(null);
  };
  const handleConfirmDelete = () => {
    if (segmentToDelete) {
      setDeleteQueue((prev) => [...prev, segmentToDelete.id]);
      deleteSegment(segmentToDelete.id);
      setDeleteConfirmOpen(false);
      setSegmentToDelete(null);
    }
  };

  const handleOpenPreview = (segment) => {
    setSelectedSegmentId(segment.uid);
    // Always start at page 1 - opening preview on a different
    // segment must not carry the previous segment's page index
    // (different row counts make the old index almost always
    // out-of-range). The BE clamps anyway, but resetting locally
    // keeps the first render correct.
    setPreviewPage(1);
    setPreviewModalOpen(true);
    previewSegmentById({
      segment_id: segment.uid,
      page: 1,
      page_size: previewPageSize,
    });
  };
  const handleClosePreviewModal = () => {
    setPreviewModalOpen(false);
    setSelectedSegmentId(null);
  };
  const handlePreviewPageChange = (newPage) => {
    setPreviewPage(newPage);
    if (selectedSegmentId) {
      previewSegmentById({ segment_id: selectedSegmentId, page: newPage, page_size: previewPageSize });
    }
  };
  const handlePreviewRowsPerPageChange = (newPageSize) => {
    setPreviewPageSize(newPageSize);
    setPreviewPage(1);
    if (selectedSegmentId) {
      previewSegmentById({ segment_id: selectedSegmentId, page: 1, page_size: newPageSize });
    }
  };

  // Sync local pagination to the BE-clamped value so a stale
  // page index doesn't cause "click next does nothing".
  useEffect(() => {
    const pag = previewData?.pagination;
    if (!pag) return;
    const current = Number(pag.current_page);
    const requested = Number(pag.requested_page ?? pag.current_page);
    if (
      Number.isFinite(current)
      && Number.isFinite(requested)
      && current !== requested
      && current !== previewPage
    ) {
      setPreviewPage(current);
    }
  }, [previewData, previewPage]);

  const activeFilterCount = (search ? 1 : 0) + (typeFilter.length > 0 ? 1 : 0);
  const clearAll = () => { setSearch(''); setTypeFilter([]); };

  const toggleType = (t) =>
    setTypeFilter((prev) => prev.includes(t) ? prev.filter((v) => v !== t) : [...prev, t]);

  if (error) {
    return (
      <Box sx={{ width: '100%' }}>
        <PageBreadcrumbs items={[{ text: 'Segments', icon: <SegmentsIcon /> }]} sx={{ mb: 2 }} />
        <Box sx={{ p: 2, textAlign: 'center' }}>
          <Typography variant="h6" color="error" gutterBottom>Error Loading Segments</Typography>
          <Typography variant="body2" sx={{ mb: 2 }}>
            {String(error).includes('plan allows a maximum of') ? (
              <>
                {error}
                <br />
                <Link href="/settings/billing" sx={{ mt: 2, display: 'inline-block' }}>
                  View Billing Options
                </Link>
              </>
            ) : error}
          </Typography>
          <Button variant="outlined" onClick={() => fetchSegments()} sx={{ mt: 1 }}>
            Try Again
          </Button>
        </Box>
      </Box>
    );
  }

  const featureColor = {
    bg: alpha(theme.palette.info.main, theme.palette.mode === 'dark' ? 0.15 : 0.10),
    fg: theme.palette.info.main,
    bd: alpha(theme.palette.info.main, 0.30),
  };
  const engColor = {
    bg: alpha(theme.palette.warning.main, theme.palette.mode === 'dark' ? 0.18 : 0.12),
    fg: theme.palette.warning.main,
    bd: alpha(theme.palette.warning.main, 0.30),
  };
  const labelColor = {
    bg: alpha(theme.palette.primary.main, theme.palette.mode === 'dark' ? 0.18 : 0.10),
    fg: theme.palette.primary.main,
    bd: alpha(theme.palette.primary.main, 0.30),
  };

  const columns = [
    {
      key: 'identity',
      label: 'ID',
      width: 130,
      render: (segment) => (
        <EntityCell
          id={segment.uid}
          tone={segLive(segment) ? 'success' : 'neutral'}
          label={segLive(segment) ? 'Real Time' : 'Historical'}
        />
      ),
    },
    {
      key: 'name',
      label: 'Segment',
      sortable: true,
      width: '24%',
      render: (segment) => (
        <Box>
          <Typography variant="body2" sx={{ fontWeight: 600, fontSize: 13, lineHeight: 1.3 }}>
            {segment.name || '—'}
          </Typography>
          {segment.description && (
            <Typography variant="caption" color="text.secondary" sx={{
              display: 'block', fontSize: 11, mt: 0.25,
              overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
              maxWidth: 360,
            }}>
              {segment.description}
            </Typography>
          )}
        </Box>
      ),
    },
    {
      key: 'features',
      label: 'Features',
      width: '20%',
      render: (segment) => {
        const feats = segment.features || [];
        const eng = segment.engineered_features || [];
        if (feats.length === 0 && eng.length === 0) {
          return <Typography variant="body2" color="text.secondary">—</Typography>;
        }
        return (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.4 }}>
            <Pillset
              items={feats}
              color={featureColor}
              subsByListing={subsByListing}
            />
            {eng.length > 0 && (
              <Pillset
                items={eng}
                color={engColor}
                icon={<AutoAwesomeIcon sx={{ fontSize: 10 }} />}
                subsByListing={subsByListing}
              />
            )}
          </Box>
        );
      },
    },
    {
      key: 'labels',
      label: 'Labels',
      width: 160,
      render: (segment) => {
        const lbls = segment.labels || [];
        if (lbls.length === 0) return <Typography variant="body2" color="text.secondary">—</Typography>;
        return (
          <Pillset
            items={lbls}
            color={labelColor}
            max={3}
            subsByListing={subsByListing}
          />
        );
      },
    },
    {
      key: 'window',
      label: 'Window',
      sortable: true,
      width: 120,
      render: (segment) => {
        const tail = segTailSeconds(segment);
        const interval = segInterval(segment);
        return (
          <Tooltip
            arrow placement="top"
            title={
              <Box sx={{ fontSize: 11 }}>
                <Box>Tail window: {formatTimeValue(tail)}</Box>
                <Box>Interval: {formatTimeValue(interval)}</Box>
              </Box>
            }
          >
            <Box>
              <Typography variant="body2" sx={{ fontSize: 13, fontVariantNumeric: 'tabular-nums' }}>
                {formatTimeValue(tail)}
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ fontSize: 10 }}>
                every {formatTimeValue(interval)}
              </Typography>
            </Box>
          </Tooltip>
        );
      },
    },
    {
      key: 'created_at',
      label: 'Created',
      sortable: true,
      width: 130,
      render: (segment) => (
        <Tooltip title={formatDate(segment.created_at)} arrow placement="top">
          <Box>
            <Typography variant="body2" sx={{ fontSize: 13 }}>
              {formatDate(segment.created_at)}
            </Typography>
            {segment.created_at && (
              <Typography variant="caption" color="text.secondary" sx={{ fontSize: 11 }}>
                {safeRelative(segment.created_at)}
              </Typography>
            )}
          </Box>
        </Tooltip>
      ),
    },
    {
      key: 'actions',
      label: '',
      width: 80,
      align: 'right',
      stopRowClick: true,
      render: (segment) => {
        const isDeleting = deleteQueue.includes(segment.uid);
        return (
          <Box sx={{ opacity: isDeleting ? 0.4 : 1 }}>
            <RowActions
              onOpen={() => handleSegmentClick(segment.uid)}
              openLabel="Open segment"
              items={[
                {
                  label: 'Preview data',
                  icon: VisibilityIcon,
                  onClick: () => handleOpenPreview(segment),
                },
                { divider: true },
                {
                  label: 'Delete',
                  icon: DeleteOutlineIcon,
                  danger: true,
                  onClick: () => handleOpenDeleteSegment(segment),
                },
              ]}
            />
          </Box>
        );
      },
    },
  ];

  const filtersNode = (
    <>
      <TextField
        size="small"
        placeholder="Search segments by name…"
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        InputProps={{
          startAdornment: <SearchIcon sx={{ fontSize: 18, mr: 1, color: 'text.secondary' }} />,
        }}
        sx={{ minWidth: 240, flex: 1 }}
      />
      <Stack direction="row" spacing={0.5} alignItems="center">
        <Typography variant="caption" color="text.secondary" sx={{
          fontWeight: 600, letterSpacing: 0.4, textTransform: 'uppercase', mr: 0.5,
        }}>
          Type
        </Typography>
        {[
          { value: 'live', label: 'Real Time' },
          { value: 'historical', label: 'Historical' },
        ].map((opt) => {
          const selected = typeFilter.includes(opt.value);
          return (
            <Chip
              key={opt.value}
              label={opt.label}
              size="small"
              onClick={() => toggleType(opt.value)}
              color={selected ? 'primary' : 'default'}
              variant={selected ? 'filled' : 'outlined'}
              sx={{ cursor: 'pointer' }}
            />
          );
        })}
      </Stack>
    </>
  );

  const actionsNode = (
    <CMButton startIcon={<AddIcon />} onClick={handleAddNewSegment} size="small">
      New Segment
    </CMButton>
  );

  return (
    <Box sx={{ width: '100%' }}>
      <PageBreadcrumbs items={[{ text: 'Segments', icon: <SegmentsIcon /> }]} mb={0.5} />

      <Typography variant="body2" color="text.secondary" sx={{ mb: 2.5, ml: 1 }}>
        Data slices your pipelines train on. Each segment defines features, labels, and a windowing strategy.
      </Typography>

      <ListingToolbar
        filters={filtersNode}
        actions={actionsNode}
        activeCount={activeFilterCount}
        onClearFilters={clearAll}
        trailing={activeFilterCount > 0 ? `${sorted.length} of ${segments.length}` : null}
      />

      <DataTable
        columns={columns}
        rows={paged}
        getRowKey={(row) => row.uid}
        onRowClick={(row) => handleSegmentClick(row.uid)}
        loading={isLoading}
        sortColumn={sortColumn}
        sortDirection={sortDirection}
        onSortChange={(key, dir) => { setSortColumn(key); setSortDirection(dir); }}
        page={page}
        rowsPerPage={rowsPerPage}
        totalItems={sorted.length}
        onPageChange={setPage}
        onRowsPerPageChange={(n) => { setRowsPerPage(n); setPage(0); }}
        emptyState={{
          icon: <SegmentsIcon sx={{ fontSize: 32 }} />,
          title: activeFilterCount > 0 ? 'No segments match your filters' : 'No Segments Yet',
          message: activeFilterCount > 0
            ? 'Try a different keyword or clear the filters.'
            : 'Segments define the data your pipelines train on. Create one to get started.',
          action: activeFilterCount > 0 ? null : (
            <CMButton startIcon={<AddIcon />} onClick={handleAddNewSegment}>
              Create Your First Segment
            </CMButton>
          ),
        }}
      />

      <Dialog open={deleteConfirmOpen} onClose={handleCloseDeleteDialog} aria-labelledby="delete-segment-title">
        <DialogTitle id="delete-segment-title">Delete Segment</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete the segment "{segmentToDelete?.name}"? This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDeleteDialog} color="primary">Cancel</Button>
          <Button onClick={handleConfirmDelete} color="error" autoFocus>Delete</Button>
        </DialogActions>
      </Dialog>

      <SegmentDataPreviewModal
        open={previewModalOpen}
        handleClose={handleClosePreviewModal}
        data={previewData}
        loading={previewLoading}
        error={previewError}
        onPageChange={handlePreviewPageChange}
        onRowsPerPageChange={handlePreviewRowsPerPageChange}
      />
    </Box>
  );
};

const mapStateToProps = (state) => ({
  payload: state.segments_list.response,
  isLoading: state.segments_list.isLoading,
  error: state.segments_list.error,
  deleteIsLoading: state.segment_delete.isLoading,
  deleteError: state.segment_delete.error,
  previewData: state.segment_preview?.response,
  previewLoading: state.segment_preview?.isLoading,
  previewError: state.segment_preview?.error,
  marketplaceSubscriptions: state.marketplace_subscriptions?.response,
});

const mapDispatchToProps = (dispatch) => ({
  fetchSegments: () => dispatch(segmentsFetch({})),
  deleteSegment: (segmentId) => dispatch(deleteSegment(segmentId)),
  previewSegmentById: (payload) => dispatch(previewSegmentByIdRequest(payload)),
  clearSegmentPreview: () => dispatch(clearPreviewState()),
  fetchMarketplaceSubscriptions: () => dispatch(fetchSubscriptions()),
});

export default connect(mapStateToProps, mapDispatchToProps)(SegmentsPage);
